import { attrPayload, clearAttr } from "../attribution.ts";
import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { boot, useApp, set, go } from "../store.ts";
import { Card, Numi } from "../ui.tsx";

type View = "login" | "signup" | "forgot" | "forgot-sent" | "reset";

export function Auth() {
  const s = useApp(); const mode = s.authMode;
  // Check for reset token in URL
  const initView = (): View => {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") ? "reset" : mode;
  };
  const [view, setView] = useState<View>(initView);
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [name, setName] = useState(""); const [phone, setPhone] = useState(""); const [wa, setWa] = useState(false);
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  // Sync view when authMode changes from outside
  useEffect(() => { if (view !== "forgot" && view !== "forgot-sent" && view !== "reset") setView(mode); }, [mode]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (view === "forgot") {
        await api("/auth/forgot", { body: { email } });
        setView("forgot-sent");
      } else if (view === "reset") {
        await api("/auth/reset", { body: { token: resetToken, password } });
        // Clear the token from URL and go to login
        window.history.replaceState({}, "", window.location.pathname);
        set({ authMode: "login" }); setView("login"); setPassword(""); setErr("");
        setErr(""); // show success via setView
        alert("Password updated. Please sign in with your new password.");
      } else {
        await api(`/auth/${view}`, { body: view === "signup" ? { email, password, name, phone, wa: !!phone.trim() && wa, attr: attrPayload() } : { email, password, name } }); if (view === "signup") clearAttr(); await boot();
      }
    }
    catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  };

  const switchView = (v: View) => { setView(v); if (v === "login") set({ authMode: "login" }); if (v === "signup") set({ authMode: "signup" }); setErr(""); };

  return (
    <div className="authw">
      <Card className="hero" style={{ gridTemplateColumns: "auto 1fr" }}><Numi size={96} mood="fly" /><div><h1>Numi</h1><p>A friendly maths buddy for children aged 6 to 10.</p></div></Card>
      <Card>
        {view === "forgot-sent"
          ? <>
              <h2 style={{ fontSize: "1.6rem" }}>Check your email</h2>
              <p className="note">If that address is in our system, we've sent a reset link. It's good for 1 hour. Check your spam folder if it doesn't arrive.</p>
              <button className="btn big" onClick={() => switchView("login")}>Back to sign in</button>
            </>
          : view === "forgot"
          ? <>
              <h2 style={{ fontSize: "1.6rem" }}>Forgot your password?</h2>
              <p className="note">Enter the email address on your account and we'll send a reset link.</p>
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label className="field"><span className="lbl">Email</span><input className="inp" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
                {err && <p className="err" role="alert">{err}</p>}
                <button className="btn yellow big" disabled={busy}>{busy ? "Sending..." : "Send reset link"}</button>
              </form>
              <p className="note"><button className="linkbtn" onClick={() => switchView("login")}>← Back to sign in</button></p>
            </>
          : view === "reset"
          ? <>
              <h2 style={{ fontSize: "1.6rem" }}>Set a new password</h2>
              <p className="note">Choose a password you haven't used before.</p>
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label className="field"><span className="lbl">New password</span><input className="inp" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
                {err && <p className="err" role="alert">{err}</p>}
                <button className="btn yellow big" disabled={busy}>{busy ? "Saving..." : "Set new password"}</button>
              </form>
            </>
          : <>
              <h2 style={{ fontSize: "1.6rem" }}>{view === "signup" ? "Create a parent account" : "Welcome back"}</h2>
              <p className="note">{view === "signup" ? "Your 7-day free trial starts now. No card needed. " : ""}Grown-ups sign in once on this device. Then your child can play without signing in.</p>
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {view === "signup" && <label className="field"><span className="lbl">Your name</span><input className="inp" id="pname" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" /></label>}
                {view === "signup" && <label className="field"><span className="lbl">Mobile number <small>(optional)</small></span><input className="inp" id="pphone" type="tel" inputMode="tel" placeholder="+91 98765 43210" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" /><small className="note">Only for important account and learning updates. Never shared.</small></label>}
                {view === "signup" && phone.trim().length > 5 && <label className="tog"><input type="checkbox" checked={wa} onChange={(e) => setWa(e.target.checked)} /> Yes, send me a friendly WhatsApp reminder if Numi has been quiet for a couple of days <small>(you can turn this off any time)</small></label>}
                <label className="field"><span className="lbl">Email</span><input className="inp" id="pemail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
                <label className="field"><span className="lbl">Password</span><input className="inp" id="ppass" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={view === "signup" ? "new-password" : "current-password"} /></label>
                {err && <p className="err" role="alert">{err}</p>}
                <button className="btn yellow big" disabled={busy}>{busy ? "Please wait..." : view === "signup" ? "Create account" : "Sign in"}</button>
              </form>
              {view === "login" && <p className="note"><button className="linkbtn" onClick={() => switchView("forgot")}>Forgot your password?</button></p>}
              <p className="note"><button className="linkbtn" onClick={() => go("landing")}>← Back to Numi home</button></p>
              <p>{view === "signup" ? "Already have an account? " : "New to Numi? "}<button className="linkbtn" onClick={() => switchView(view === "signup" ? "login" : "signup")}>{view === "signup" ? "Sign in" : "Create an account"}</button></p>
            </>}
      </Card>
    </div>
  );
}
